// @ts-nocheck
import CircularProgress from '@mui/material/CircularProgress';
import UnsavedChangesModal from '../../Trip/Itinerary/UnsavedChangesModal/UnsavedChangesModal';
import { useTranslation } from 'react-i18next';
import { XGButton, XGSelect, XGInput } from 'xg-cl';
import XGTextarea from '../../ProseTextarea/ProseTextarea';
import {
  REQUIRED,
  OPTIONAL,
  INTERNAL
} from '../../../constants/passengerFieldStatuses';
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormControl,
  Radio,
  RadioGroup
} from '@mui/material';
import { X, PlusCircle, Trash, Books } from '@phosphor-icons/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState, useEffect, useRef } from 'react';
import * as PropTypes from 'prop-types';
import './PassengerFieldSlideout.css';

/**
 * PassengerFieldSlideout component provides a slide-out panel for adding or editing passenger fields.
 * Allows users to add or edit trip passenger fields.
 */
const PassengerFieldSlideout = ({
  isOpen,
  isGlobalSettings = false,
  isAddAnswerDisabled,
  onClose,
  onSave,
  onSaveToLibrary = null,
  isSubmitting = false,
  isSubmittingToLibrary = false,
  fieldTypes = [],
  fieldForm = null,
  setFieldForm,
  setIsAddAnswerDisabled,
  ...rest
}) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const { t } = useTranslation(['passengers', 'common', 'settings']);
  const { enableInternalPassengerFields, enablePax2ndRound } = useFlags();
  const {
    trippassengerfieldoption = [],
    passengerfieldoption = [],
    selectedFieldType,
    isEditing
  } = fieldForm;

  const fieldOptions = isGlobalSettings
    ? passengerfieldoption
    : trippassengerfieldoption;

  const optionsKey = isGlobalSettings
    ? 'passengerfieldoption'
    : 'trippassengerfieldoption';
  const shouldShowLegacyStatusControls =
    isGlobalSettings && enableInternalPassengerFields && !enablePax2ndRound;
  const shouldShowInternalFieldToggle = isGlobalSettings && !enablePax2ndRound;
  const isReadOnlySystemField = Boolean(fieldForm?.issystemfield);
  const initialFormRef = useRef(null);

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    let newFieldOptions = [];

    if (name === 'fieldType') {
      const foundFieldType = fieldTypes.find(
        (fieldType) => fieldType?.label === value
      );

      const { allowoptions, key } = foundFieldType;

      if (allowoptions) {
        //Boolean should only have 2 options
        if (key === 'boolean') {
          newFieldOptions =
            fieldOptions?.length > 2
              ? [fieldOptions[0], fieldOptions[1]]
              : fieldOptions;

          setIsAddAnswerDisabled(fieldOptions?.length >= 2);
        } else {
          setIsAddAnswerDisabled(false);
          newFieldOptions = fieldOptions;
        }
      }

      setFieldForm((prev) => ({
        ...prev,
        selectedFieldType: foundFieldType,
        [optionsKey]: newFieldOptions,
        error: { ...prev.error, selectedFieldType: '' }
      }));
    } else {
      setFieldForm((prev) => ({
        ...prev,
        [name]: value,
        error: { ...prev.error, [name]: '' }
      }));
    }
  };

  const handleOptionChange = (evt, index) => {
    const { name, value } = evt.target;
    const newOptions = [...fieldOptions];

    newOptions[index][name] = value;
    newOptions[index].error = '';

    setFieldForm((prev) => ({
      ...prev,
      [optionsKey]: newOptions
    }));
  };

  const handleAddOption = () => {
    const newOptions = [
      ...fieldOptions,
      {
        title: '',
        sequence: fieldOptions.length + 1,
        isselected: false
      }
    ];
    const shouldAddAnswer =
      selectedFieldType?.key === 'boolean' && fieldOptions.length >= 2
        ? false
        : true;

    if (shouldAddAnswer) {
      setFieldForm((prev) => ({
        ...prev,
        [optionsKey]: newOptions,
        error: { ...prev.error, fieldOptions: '' }
      }));
    }

    if (selectedFieldType?.key === 'boolean' && newOptions.length === 2) {
      setIsAddAnswerDisabled(true);
    }
  };

  const handleRemoveOption = (sequence = 0) => {
    const newOptions = [...fieldOptions]
      ?.filter((option) => option.sequence !== sequence)
      ?.map((option, index) => {
        return {
          ...option,
          sequence: index + 1
        };
      });

    setFieldForm((prev) => ({
      ...prev,
      [optionsKey]: newOptions
    }));

    if (selectedFieldType?.key === 'boolean' && newOptions.length < 2) {
      setIsAddAnswerDisabled(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    onSave();
  };

  const handleRadioChange = (event) => {
    const { value } = event.target;

    setFieldForm({
      ...fieldForm,
      isrequired: value === REQUIRED,
      isoptional: value === OPTIONAL,
      isinternal: value === INTERNAL
    });
  };

  const handleInternalFieldChange = (event) => {
    const { checked } = event.target;

    setFieldForm((prev) => ({
      ...prev,
      isinternal: checked,
      isrequired: false,
      isoptional: checked ? false : true
    }));
  };

  const serializeForDirtyCheck = (form) => {
    if (!form) return '';
    // eslint-disable-next-line no-unused-vars
    const { error, ...rest } = form;
    return JSON.stringify(rest);
  };

  const handleUnsavedModalAction = (action) => {
    switch (action) {
      case 'continue':
        setShowUnsavedModal(false);
        break;
      case 'discard':
        setHasUnsavedChanges(false);
        setShowUnsavedModal(false);
        onClose();
        break;
      case 'save':
        handleSave();
        break;
      default:
        setShowUnsavedModal(false);
        break;
    }
  };

  // Capture initial snapshot when slideout opens
  useEffect(() => {
    if (isOpen && fieldForm) {
      initialFormRef.current = serializeForDirtyCheck(fieldForm);
    }
    if (!isOpen) {
      initialFormRef.current = null;
      setHasUnsavedChanges(false);
      setShowUnsavedModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Keep hasUnsavedChanges in sync with isDirty on every fieldForm change
  useEffect(() => {
    if (!initialFormRef.current || !fieldForm) return;
    const isDirty =
      serializeForDirtyCheck(fieldForm) !== initialFormRef.current;
    setHasUnsavedChanges(isDirty);
  }, [fieldForm]);

  // isValid — required fields must be filled before save is enabled
  const isValid =
    Boolean(fieldForm?.selectedFieldType) && Boolean(fieldForm?.title?.trim());

  // isDirty — derived from ref comparison
  const isDirty = initialFormRef.current
    ? serializeForDirtyCheck(fieldForm) !== initialFormRef.current
    : false;

  const isSaveDisabled =
    isSubmitting ||
    isReadOnlySystemField ||
    isSubmittingToLibrary ||
    !isValid ||
    !isDirty;

  return (
    <>
      <Drawer
        ModalProps={{
          disableEscapeKeyDown: true
        }}
        hideBackdrop
        open={isOpen}
        onClose={handleClose}
        title={`${isEditing ? t('editPassengerField') : t('newPassengerField')}`}
        className={`trip-passenger-fields-slideout__drawer ${isOpen ? 'drawer-open' : ''}`}
        anchor={'right'}
        id='trip-passenger-fields-slideout-drawer'
        position='right'
        {...rest}
      >
        <Box className='trip-passenger-fields-slideout'>
          <Box className='trip-passenger-fields-slideout__header'>
            <Box className='trip-passenger-fields-slideout__header-title'>
              <Typography
                variant='h6'
                className='trip-passenger-fields-slideout__title'
              >
                {`${isEditing ? t('editPassengerField') : t('newPassengerField')}`}
              </Typography>
              <XGButton
                className='trip-passenger-fields-slideout__close-btn'
                type='tertiary'
                size='small'
                onClick={handleClose}
                startIcon={<X size={21} />}
                variant='text'
                id='detailed-view-edit-day-close-button'
                disabled={isSubmitting || isSubmittingToLibrary}
              />
            </Box>
          </Box>

          <Box className='trip-passenger-fields-slideout__content'>
            <Box className='trip-passenger-fields-slideout__fields-container'>
              <Box className='w-100' style={{ display: 'flex', gap: '16px' }}>
                <Box className='w-40'>
                  <Typography
                    variant='subtitle1'
                    className='trip-passenger-fields-slideout__section-title'
                  >
                    {t('fieldType')}
                  </Typography>
                  <XGSelect
                    id='trip-passenger-field-field-type-select'
                    name='fieldType'
                    size='small'
                    value={selectedFieldType?.label ?? ''}
                    menuItems={fieldTypes ?? []}
                    onChange={handleChange}
                    error={
                      Object.hasOwn(
                        fieldForm?.error ?? {},
                        'selectedFieldType'
                      ) && fieldForm?.error?.selectedFieldType !== ''
                    }
                    disabled={
                      isSubmitting ||
                      isReadOnlySystemField ||
                      isSubmittingToLibrary
                    }
                  />
                  {Object.hasOwn(fieldForm?.error ?? {}, 'selectedFieldType') &&
                    fieldForm?.error?.selectedFieldType !== '' && (
                      <span className='text-sm-regular error-text'>
                        {fieldForm?.error?.selectedFieldType}
                      </span>
                    )}
                </Box>
                <Box className='w-60 trip-passenger-fields-slideout__form-group'>
                  <Typography
                    variant='subtitle1'
                    className='trip-passenger-fields-slideout__section-title'
                  >
                    {t('fieldTitle')}
                  </Typography>
                  <XGInput
                    id='passenger-field-title-input'
                    name='title'
                    placeholder={t('enterFieldTitle')}
                    type='text'
                    value={fieldForm?.title ?? ''}
                    onChange={handleChange}
                    inputProps={{
                      maxLength: 100
                    }}
                    error={
                      Object.hasOwn(fieldForm?.error ?? {}, 'title') &&
                      fieldForm?.error?.title !== ''
                    }
                    hint={fieldForm?.error?.title}
                    disabled={
                      isSubmitting ||
                      isReadOnlySystemField ||
                      isSubmittingToLibrary
                    }
                  />
                  <span className='field_title-length-hint length-hint text-sm-medium'>
                    {fieldForm?.title?.length}/100
                  </span>
                </Box>
              </Box>

              <Box className='w-100 trip-passenger-fields-slideout__form-group'>
                <Typography
                  variant='subtitle1'
                  className='trip-passenger-fields-slideout__section-title'
                >
                  {t('fieldDescription')}
                </Typography>
                <XGTextarea
                  id='trip-passenger-field-title-input-decription'
                  label={null}
                  placeholder={t('enterFieldDescription')}
                  value={fieldForm?.description ?? ''}
                  minRows={3}
                  maxRows={4}
                  inputProps={{
                    maxLength: 500
                  }}
                  name='description'
                  sx={{
                    width: '100%',
                    height: 'auto',
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}
                  onChange={handleChange}
                  disabled={
                    isSubmitting ||
                    isReadOnlySystemField ||
                    isSubmittingToLibrary
                  }
                />
                <span
                  className='field_title-length-hint length-hint text-sm-medium'
                  style={{ marginTop: '58px' }}
                >
                  {fieldForm?.description?.length}/500
                </span>
              </Box>

              {selectedFieldType?.allowoptions &&
                fieldOptions &&
                fieldOptions.length > 0 && (
                  <Box className='w-100 trip-passenger-fields-slideout__options-container'>
                    {fieldOptions.map((option, index) => {
                      return (
                        <Box
                          className='w-100 trip-passenger-fields-slideout__option-row'
                          key={index}
                        >
                          <Box
                            className='w-55'
                            style={{ position: 'relative' }}
                          >
                            <XGInput
                              id={`trip-passenger-field-option-input-${index}`}
                              label={`${t('answer')} #${option?.sequence ?? index}`}
                              name='title'
                              placeholder={t('enterAnswerTitle')}
                              type='text'
                              value={option?.title ?? ''}
                              onChange={(evt) => handleOptionChange(evt, index)}
                              inputProps={{
                                maxLength: 100
                              }}
                              error={
                                Object.hasOwn(option ?? {}, 'error') &&
                                option?.error !== ''
                              }
                              hint={option?.error}
                              disabled={
                                isSubmitting ||
                                isReadOnlySystemField ||
                                isSubmittingToLibrary
                              }
                            />
                          </Box>
                          <Box>
                            <IconButton
                              aria-label='Delete'
                              onClick={() =>
                                handleRemoveOption(option?.sequence)
                              }
                              disabled={
                                isSubmitting ||
                                isReadOnlySystemField ||
                                isSubmittingToLibrary
                              }
                            >
                              <Trash size={24} color='#1361D8' />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

              {selectedFieldType?.allowoptions && (
                <Box>
                  <XGButton
                    className={'trip__passenger-fields__add-answer-button'}
                    id='trip-passenger-field-add-answer-button'
                    data-testid='trip-passenger-field-add-answer-button'
                    text={t('addAnswer')}
                    type='primary'
                    size='medium'
                    variant='outlined'
                    onClick={handleAddOption}
                    disabled={
                      isSubmitting ||
                      isAddAnswerDisabled ||
                      isReadOnlySystemField ||
                      isSubmittingToLibrary
                    }
                    startIcon={
                      <PlusCircle
                        className={`trip__passenger-fields__action-icon ${isAddAnswerDisabled ? 'trip__passenger-fields__action-icon__disabled' : ''}`}
                      />
                    }
                  />
                  {Object.hasOwn(fieldForm?.error ?? {}, 'fieldOptions') &&
                    fieldForm?.error?.fieldOptions !== '' && (
                      <span className='text-sm-regular error-text'>
                        {fieldForm?.error?.fieldOptions}
                      </span>
                    )}
                </Box>
              )}
            </Box>

            {shouldShowLegacyStatusControls && (
              <Box className='w-100 trip-passenger-fields-slideout__status '>
                {isGlobalSettings && (
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          id='trip-passenger-field-default-checkbox'
                          name='debounce'
                          checked={!fieldForm?.ishidden ?? false}
                          disabled={
                            isSubmitting ||
                            isReadOnlySystemField ||
                            isSubmittingToLibrary
                          }
                          onChange={(e) => {
                            const { checked } = e.target;

                            setFieldForm((prev) => ({
                              ...prev,
                              ishidden: checked ? false : true
                            }));
                          }}
                          sx={{ padding: '0px', marginRight: '8px' }}
                        />
                      }
                      label={t(
                        'passengers:setFieldAsDefault',
                        'Set as default for new trips'
                      )}
                      style={{ marginLeft: '0px' }}
                    />
                  </Box>
                )}

                <Box className='trip-passenger-fields-slideout__display-container'>
                  <Box className='trip-passenger-fields-slideout__display-radio-group'>
                    <FormControl>
                      <span
                        className='text-sm-semibold'
                        style={{ color: '#111828' }}
                      >
                        {t('settings:display')}
                      </span>
                      <RadioGroup
                        row
                        aria-labelledby='display-radio-group-label'
                        name='display-radio-group'
                      >
                        <FormControlLabel
                          value={REQUIRED}
                          control={<Radio />}
                          label={t('common:required')}
                          checked={fieldForm?.isrequired ?? false}
                          onChange={handleRadioChange}
                          disabled={
                            isSubmitting ||
                            isReadOnlySystemField ||
                            isSubmittingToLibrary
                          }
                        />
                        <FormControlLabel
                          value={OPTIONAL}
                          control={<Radio />}
                          label={t('common:optional')}
                          checked={fieldForm?.isoptional ?? false}
                          onChange={handleRadioChange}
                          disabled={
                            isSubmitting ||
                            isReadOnlySystemField ||
                            isSubmittingToLibrary
                          }
                        />
                        <FormControlLabel
                          value={INTERNAL}
                          control={<Radio />}
                          label={t('common:internal')}
                          checked={fieldForm?.isinternal ?? false}
                          onChange={handleRadioChange}
                          disabled={
                            isSubmitting ||
                            isReadOnlySystemField ||
                            isSubmittingToLibrary
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>
              </Box>
            )}

            {shouldShowInternalFieldToggle && (
              <Box className='w-100 trip-passenger-fields-slideout__status'>
                <FormControlLabel
                  control={
                    <Checkbox
                      id='trip-passenger-field-is-internal-checkbox'
                      checked={fieldForm?.isinternal ?? false}
                      onChange={handleInternalFieldChange}
                      disabled={
                        isSubmitting ||
                        isReadOnlySystemField ||
                        isSubmittingToLibrary
                      }
                      sx={{ padding: '0px', marginRight: '8px' }}
                    />
                  }
                  label={t('markAsInternalField')}
                  style={{ marginLeft: '0px' }}
                />
                <span className='trip-passenger-fields-slideout__status-help'>
                  {t('internalFieldHelp')}
                </span>
              </Box>
            )}
          </Box>

          <Box className='trip-passenger-fields-slideout__footer'>
            <Box className='trip-passenger-fields-slideout__footer-actions'>
              <Box>
                {!isGlobalSettings && (
                  <XGButton
                    id='line-item-edit-slideout__add-to-library-btn'
                    type='secondary'
                    variant='outlined'
                    text={t('library:saveToLibrary')}
                    startIcon={<Books size={20} />}
                    onClick={onSaveToLibrary}
                    disabled={isSubmitting || isSubmittingToLibrary}
                  />
                )}
              </Box>
              <Box className='trip-passenger-fields-slideout__primary-actions'>
                <XGButton
                  data-testid='additional-content-cancel-button'
                  id='additional-content-cancel-button'
                  text={t('common:cancel')}
                  type='secondary'
                  size='medium'
                  variant='outlined'
                  onClick={handleClose}
                  disabled={isSubmitting || isSubmittingToLibrary}
                />

                <Box className='relative-position'>
                  <XGButton
                    id='trip-passenger-fields-slideout-save-button'
                    text={t('common:saveChanges')}
                    type='primary'
                    size='medium'
                    variant='contained'
                    onClick={handleSave}
                    disabled={isSaveDisabled}
                  />
                  {isSubmitting && (
                    <CircularProgress
                      id='trip-passenger-fields-slideout-loader-spinner'
                      size={24}
                      className='spinner spinner__primary'
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Drawer>
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onAction={handleUnsavedModalAction}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

PassengerFieldSlideout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  isAddAnswerDisabled: PropTypes.bool,
  isSubmittingToLibrary: PropTypes.bool,
  setIsAddAnswerDisabled: PropTypes.func,
  fieldTypes: PropTypes.array,
  fieldForm: PropTypes.object,
  setFieldForm: PropTypes.func,
  isGlobalSettings: PropTypes.bool,
  onSaveToLibrary: PropTypes.func
};

export default PassengerFieldSlideout;
